import { api } from '@/shared/api/api';
import { TourComponent } from '@/shared/components';
import SearchField from '@/shared/components/SearchField/SearchField';
import { useResize } from '@/shared/utils';
import { on, removeAllListeners } from '@/socket';
import { MoreOutlined, RollbackOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Checkbox,
  Dropdown,
  FloatButton,
  Form,
  Input,
  message,
  Modal,
  notification,
  Popconfirm,
  Segmented,
  Space,
  Table,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTour } from '../../useTour';
import { fetchUserList } from './api/fetchUserList.api';
import { BlockDeviceModalMobile } from './components/BlockDeviceModalMobile/BlockDeviceModalMobile';
import ChangeLicenseModal from './components/changeOfLicenseModal/changeOfLicenseModal';
import CurrentUserModal from './components/CurrentUserModal/CurrentUserModal';
import { DebounceSelect } from './components/DebounceSelect/DebounceSelect';
import { DeviceIcon } from './components/DeviceIcon/DeviceIcon';
import { FeatureModal } from './components/FeatureModal/FeatureModal';
import GenerateNewIdModal from './components/GenerateNewIdModal/GenerateNewIdModal';
import { LocalizedModal } from './components/LocalizedModal/LocalizedModal';
import TrustUserGroup from './components/TrustUserGroup/TrustUserGroup';
import {
  ACTION_TYPES,
  ACTUAL_ACTION_ITEMS,
  DEVICES_TYPES,
  DEVICES_TYPES_LIST,
} from './ForwarderTable.constants';
import styles from './ForwarderTable.module.css';
import { changeStatusDevice } from './utils/changeStatusDevice';
import { formatTime } from './utils/formatTime';

const ForwarderTable = () => {
  const [form] = Form.useForm();
  const { isMobile } = useResize();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [data, setData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [updatedData, setUpdatedData] = useState();
  const [nameBtn, setNameBtn] = useState(null);
  const [clickRowId, setClickRowId] = useState();
  const [searcher, setSearcher] = useState(false);
  const [loading, setLoading] = useState(false);
  const userInfo = useSelector(state => state.userInfo.userState);
  const isAdmin = useSelector(state => state.userInfo.isAdmin);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalData, setTotalData] = useState(10);
  const [value, setValue] = useState();

  const [actualSegment, setActualSegment] = useState(DEVICES_TYPES.ACTUAL);
  const [isGenerateIdOpen, setGenerateIdOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionModalText, setActionModalText] = useState('');
  const [handleActionFunction, setHandleActionFunction] = useState(() => () => {});
  const [trustUserModalOpen, setTrustUserModalOpen] = useState(false);
  const [trustUserGroupData, setTrustUserGroupData] = useState({});
  const [currentDeviceType, setCurrentDeviceType] = useState('');
  const abortControllerRef = useRef(null);
  const {
    generateIdRef,
    licenseRef,
    changeUserRef,
    editOtherRef,
    actionsRef,
    searchRef,
    steps,
    startTour,
    handleTourChange,
    handleClose,
    isTourOpen,
    currentStep,
  } = useTour(isAdmin, data.length > 0);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    on('updateDeviceList', device => {
      const newData = changeStatusDevice(data, device);

      if (newData) {
        setData(newData);
      }
    });

    return function cleanup() {
      removeAllListeners('updateDeviceList');
    };
  }, [data]);

  const handleMenuClick = async (e, id) => {
    const { data: resData } = await api.patch(`/api/device/edit`, {
      id: id,
      type: e.key,
    });

    if (resData) {
      await fetchListDevices(actualSegment);
    }
  };

  const handleActionsMenuClick = async (e, record) => {
    switch (e.key) {
      case ACTION_TYPES.TRANSLATION_REQUEST:
        setActionModalText(`Запросить трансляцию ${record.device_id}?`);
        setHandleActionFunction(() => () => {
          if (record.connectionStatus) {
            sendTranslationRequest(record.device_id);
          } else {
            openNotification('Запрос трансляции', `Устройство ${record.device_id} не в сети!`);
          }
        });
        setIsActionModalOpen(true);
        break;

      case ACTION_TYPES.TRUST_USER_GROUP:
        setTrustUserGroupData({
          device_id: record.device_id,
          readOnlySubs: record.readOnlySubs,
        });
        showTrustUserModal();
        break;

      case ACTION_TYPES.CLEAR_LOGIN:
        setActionModalText(`Очистить пароль для ${record.device_id}?`);
        setHandleActionFunction(() => () => clearLogin(record.id));
        setIsActionModalOpen(true);
        break;

      case ACTION_TYPES.ROUTER:
      case ACTION_TYPES.PANEL:
      case ACTION_TYPES.TABLET:
        handleMenuClick(e, record.id);
        break;

      case ACTION_TYPES.BLOCK_UNBLOCK_DEVICE:
        setActionModalText(
          `${actualSegment === DEVICES_TYPES.ACTUAL ? 'Заблокировать' : 'Разблокировать'} устройство ${
            record.call_sign || record.device_id
          }?`
        );
        setHandleActionFunction(
          () => () =>
            actualSegment === DEVICES_TYPES.ACTUAL
              ? blockUnblockDevice(record, '/api/device/block_device')
              : blockUnblockDevice(record, '/api/device/unblock_device')
        );
        setIsActionModalOpen(true);
        break;

      case ACTION_TYPES.DELETE_DEVICE:
        setActionModalText(`Удалить устройство ${record.device_id}?`);
        setHandleActionFunction(() => async () => {
          softDelete(record);
          setValue('');
          await fetchListDevices(actualSegment);
        });
        setIsActionModalOpen(true);
        break;
      default:
        break;
    }
  };

  const fetchListUsersTable = async () => {
    try {
      const { data: resData } = await api.get(`/api/users/getList`);
      return setUsersData(resData.map((el, i) => ({ ...el, key: i + 1 })).filter(el => el.id !== userInfo.userId));
    } catch (error) {
      console.log('error-fetchDataChartPage >>>', error);
    }
  };

  const showTrustUserModal = async () => {
    await fetchListUsersTable();
    setTrustUserModalOpen(true);
  };

  const columns = isMobile
    ? [
        {
          title: 'ID устройства',
          dataIndex: 'device_id',
          editable: true,
          key: 'device_id',
          render: (text, record) => {
            return record.device_id ? (
              <div>
                <Badge dot={record.connectionStatus} color='green'>
                  <Checkbox checked={record.password}>{record.device_id}</Checkbox>
                </Badge>
              </div>
            ) : (
              <Badge dot={record.connectionStatus} color='green'>
                <Checkbox checked={record.password}>Отсутствует</Checkbox>
              </Badge>
            );
          },
        },
        {
          title: 'Действия',
          dataIndex: 'action',
          editable: true,
          key: 'action',
          render: (text, record) => {
            return (
              <div
                style={{ display: 'block', textAlign: 'center', left: '50%', top: '50%' }}
                ref={data.indexOf(record) % 10 === 0 ? actionsRef : null}
              >
                {actualSegment === DEVICES_TYPES.DELETED ? (
                  <Popconfirm
                    title='Вернуть устройства в актуальные?'
                    description={`Вы уверены что хотите вернуть устройство ${record.call_sign || record.device_id}?`}
                    onConfirm={() => revertDevice(record)}
                    okText='Подтвердить'
                    cancelText='Отмена'
                  >
                    <Button type='primary' icon={<RollbackOutlined />}></Button>
                  </Popconfirm>
                ) : (
                  <ul style={{ padding: 0 }}>
                    <li style={{ display: 'block', marginBottom: '1em' }}>
                      <LocalizedModal
                        id={record.id}
                        size='small'
                        fetchListDevices={fetchListDevices}
                        setValue={setValue}
                        actualSegment={actualSegment}
                      />
                    </li>
                    {actualSegment === DEVICES_TYPES.ACTUAL ? (
                      <>
                        <li style={{ display: 'block', marginBottom: '1em' }}>
                          <Button
                            style={{ width: '100%', position: 'relative' }}
                            type='default'
                            onClick={() => {
                              clearLogin(record.id);
                            }}
                            size='small'
                          >
                            Очистить пароль
                          </Button>
                        </li>
                        <li style={{ display: 'block', marginBottom: '1em' }}>
                          <TrustUserGroup
                            device_id={record.device_id}
                            readOnlySubs={record.readOnlySubs}
                            fetchListDevices={fetchListDevices}
                            actualSegment={actualSegment}
                            userInfo={userInfo}
                            isMobile={isMobile}
                            trustUserModalOpen={trustUserModalOpen}
                            setTrustUserModalOpen={setTrustUserModalOpen}
                            usersData={usersData}
                            fetchListUsersTable={fetchListUsersTable}
                          />
                        </li>
                      </>
                    ) : null}
                    <li style={{ display: 'block', marginBottom: '1em' }}>
                      <FeatureModal
                        id={record.id}
                        device_id={record.device_id}
                        password={record.password}
                        call_sign={record.call_sign}
                        army_post={record.army_post}
                        type={record.type}
                        tg={record.tg}
                      />
                    </li>
                    <li style={{ display: 'block', marginBottom: '1em' }}>
                      <BlockDeviceModalMobile
                        id={record.id}
                        device_id={record.device_id}
                        password={record.password}
                        call_sign={record.call_sign}
                        army_post={record.army_post}
                        type={record.type}
                        tg={record.tg}
                      />
                    </li>
                  </ul>
                )}
              </div>
            );
          },
        },
      ]
    : [
        {
          title: 'Android id',
          dataIndex: 'device_id',
          editable: true,
          key: 'device_id',
          width: 150,
          render: (text, record) => (
            <>
              <DeviceIcon
                type={record.type}
                actualSegment={actualSegment}
                connectionStatus={record.connectionStatus}
                formatTime={formatTime}
                disconnectTime={record.disconnectTime}
              />
              {record.device_id}
            </>
          ),
        },
        {
          title: 'Позывной',
          dataIndex: 'call_sign',
          key: 'call_sign',
          editable: true,
          width: 150,
          render: (text, record) => {
            return (
              <div ref={editOtherRef}>
                {clickRowId === record.id && 'call_sign' === nameBtn && actualSegment === DEVICES_TYPES.ACTUAL ? (
                  <>
                    <Form.Item className={styles.formItem} name={'call_sign'}>
                      <Input
                        className={styles.tableInput}
                        ref={input => input && input.focus()}
                        onBlur={() => {
                          setClickRowId();
                          setNameBtn();
                        }}
                        defaultValue={record.call_sign}
                        value={text}
                      />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        height: '25px',
                      }}
                      onClick={() => {
                        setClickRowId(record.id);
                        setNameBtn('call_sign');
                      }}
                    >
                      {text}
                    </div>
                  </>
                )}
              </div>
            );
          },
        },
        {
          title: 'Военная часть',
          dataIndex: 'army_post',
          editable: true,
          key: 'army_post',
          width: 150,
          render: (text, record) => {
            return (
              <div ref={editOtherRef}>
                {clickRowId === record.id && 'army_post' === nameBtn && actualSegment === DEVICES_TYPES.ACTUAL ? (
                  <Form.Item className={styles.formItem} name={'army_post'}>
                    <Input
                      className={styles.tableInput}
                      ref={input => input && input.focus()}
                      onBlur={() => {
                        setClickRowId();
                        setNameBtn();
                      }}
                      defaultValue={record.army_post}
                      value={text}
                    />
                  </Form.Item>
                ) : (
                  <>
                    <div
                      style={{
                        height: '25px',
                      }}
                      onClick={() => {
                        setClickRowId(record.id);
                        setNameBtn('army_post');
                      }}
                    >
                      {text}
                    </div>
                  </>
                )}
              </div>
            );
          },
        },
        {
          title: 'Пользователь',
          dataIndex: 'actualUser',
          editable: true,
          key: 'actualUser',
          width: 150,
          render: (text, record) => {
            return (
              <div ref={changeUserRef}>
                <Space>
                  {record['user.id'] || record?.user?.id
                    ? record['user.name'] || record?.user?.name || record['user.email'] || record?.user?.email
                    : 'Отсутствует'}
                  {actualSegment === DEVICES_TYPES.ACTUAL && (
                    <>
                      <CurrentUserModal
                        id={record.id}
                        fetchListDevices={fetchListDevices}
                        actualSegment={actualSegment}
                        DebounceSelect={DebounceSelect}
                        fetchUserList={fetchUserList}
                      />
                    </>
                  )}
                </Space>
              </div>
            );
          },
        },
        {
          title: 'Телеграм',
          dataIndex: 'tg',
          editable: true,
          key: 'tg',
          width: 150,
          render: (text, record) => {
            return (
              <div style={{ textAlign: 'center' }} ref={editOtherRef}>
                {clickRowId === record.id && 'tg' === nameBtn && actualSegment === DEVICES_TYPES.ACTUAL ? (
                  <Form.Item className={styles.formItem} name={'tg'}>
                    <Input
                      className={styles.tableInput}
                      ref={input => input && input.focus()}
                      onBlur={() => {
                        setClickRowId();
                        setNameBtn();
                      }}
                      defaultValue={record.tg}
                      value={text}
                    />
                  </Form.Item>
                ) : (
                  <>
                    <div
                      style={{
                        height: '25px',
                      }}
                      onClick={() => {
                        setClickRowId(record.id);
                        setNameBtn('tg');
                      }}
                    >
                      <a href={`https://t.me/${text}`} target={'_blank'} rel='noreferrer'>
                        {text}
                      </a>
                    </div>
                  </>
                )}
              </div>
            );
          },
        },
        {
          title: 'Серия',
          dataIndex: 'serial',
          editable: false,
          key: 'serial',
          width: 150,
          render: (text, record) => <Space>{record.serial}</Space>,
        },
        {
          title: 'Лицензия до',
          dataIndex: 'license_expires',
          editable: true,
          key: 'license_expires',
          width: 150,
          render: (text, record) => {
            const licenseExpires = record['license_store.license_expires'] || record?.license_store?.license_expires;
            const formattedDate = licenseExpires ? dayjs(licenseExpires).format('DD.MM.YYYY HH:mm:ss') : 'Отсутствует';

            return (
              <div ref={licenseRef}>
                <Space>
                  {formattedDate}
                  {actualSegment === DEVICES_TYPES.ACTUAL && isAdmin && (
                    <ChangeLicenseModal
                      id={record.id}
                      fetchListDevices={fetchListDevices}
                      actualSegment={actualSegment}
                      DebounceSelect={DebounceSelect}
                      fetchUserList={fetchUserList}
                      licenseExpires={record['license_store.license_expires']}
                    />
                  )}
                </Space>
              </div>
            );
          },
        },
        {
          title: '',
          dataIndex: 'action',
          key: 'action',
          editable: true,
          width: '28px',
          fixed: 'right',
          render: (text, record) => {
            return (
              <div ref={actionsRef}>
                <Space>
                  {actualSegment === DEVICES_TYPES.ACTUAL && (
                    <Dropdown
                      menu={{
                        items: ACTUAL_ACTION_ITEMS(currentDeviceType),
                        onClick: async e => await handleActionsMenuClick(e, record),
                        onOpenChange: () => setCurrentDeviceType(record.type),
                      }}
                      trigger={['click']}
                    >
                      <Button style={{ padding: 0, margin: 0, display: 'inline-block', width: '30px', height: '30px' }}>
                        <MoreOutlined style={{ fontSize: '20px', paddingTop: '2px' }} />
                      </Button>
                    </Dropdown>
                  )}

                  {actualSegment === 'online' && (
                    <Dropdown
                      menu={{
                        items: ACTUAL_ACTION_ITEMS(currentDeviceType),
                        onClick: async e => await handleActionsMenuClick(e, record),
                      }}
                      trigger={['click']}
                    >
                      <Button
                        onClick={e => e.preventDefault()}
                        style={{ padding: 0, margin: 0, display: 'inline-block', width: '30px', height: '30px' }}
                      >
                        <MoreOutlined style={{ fontSize: '20px', paddingTop: '2px' }} />
                      </Button>
                    </Dropdown>
                  )}
                </Space>
              </div>
            );
          },
        },
      ].filter(
        item =>
          !(
            (item.title === 'Тип устройства' && actualSegment === DEVICES_TYPES.BLOCK) ||
            (item.title === 'Тип устройства' && actualSegment === DEVICES_TYPES.DELETED)
          )
      );

  const sendTranslationRequest = async device_id => {
    try {
      api
        .post('/api/device/sendTranslationRequest', {
          device_id: device_id,
          initiator: userInfo.email,
        })
        .then(({ data: res }) => {
          if (res.error) {
            openNotification('Запрос трансляции', 'Ошибка при отправке запроса на начало трансляции');
          } else {
            openNotification('Запрос трансляции', `Запрос на трансляцию с устройства ${device_id} успешно отправлен!`);
          }
        })
        .catch(() => console.log(`Ошибка запроса`));
    } catch (error) {
      console.log('error sendTranslationRequest >>>', error);
      openNotification('Запрос трансляции', 'Ошибка при отправке запроса на начало трансляции');
    }
  };

  const fetchListDevices = useCallback(
    async segment => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        api
          .get(
            searcher
              ? `/api/device/find_with/${value}?actualSegment=${actualSegment}&offset=${page * 10}`
              : `/api/device/${segment}?offset=${page * 10}`,
            {
              signal: abortController.signal,
            }
          )
          .then(({ data: resData }) => {
            setTotalData(resData.count);
            setData(resData.rows?.length ? resData.rows.map((el, i) => ({ ...el, key: i + 1 })) : []);
          })
          .finally(() => setLoading(false));
      } catch (error) {
        console.log('error-fetchDataForwarder >>>', error);
      }
    },
    [isAdmin, page, userInfo, searcher]
  );

  const fetchOnlineDevices = useCallback(async () => {
    try {
      setPage(1);
      setLoading(true);
      await fetchListDevices(DEVICES_TYPES.ONLINE);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchListDevices]);

  useEffect(() => {
    if (!searcher) {
      fetchListDevices(actualSegment);
    }
  }, [actualSegment, fetchListDevices, searcher]);

  useEffect(() => {
    if (updatedData) {
      const newData = data.current?.map(item => {
        if (item.id === updatedData.id) {
          return { ...item, [updatedData.nameKey]: updatedData.values };
        } else {
          return item;
        }
      });
      setData(newData);
      setUpdatedData(null);
    }
  }, [updatedData]);

  const ActionModal = ({ text, callback }) => {
    const handleOk = () => {
      setIsActionModalOpen(false);
      callback();
    };
    const handleCancel = () => {
      setIsActionModalOpen(false);
    };
    return (
      <Modal title={text} open={isActionModalOpen} onOk={handleOk} onCancel={handleCancel} cancelText='Отмена'></Modal>
    );
  };

  const openNotification = (title, text) => {
    notificationApi.info({
      message: <b>{title}</b>,
      description: text,
      placement: 'topRight',
    });
  };

  const blockUnblockDevice = async (params, url) => {
    const { data: response } = await api.post(url, { device_id: params.device_id });
    if (response) openNotification('Блокировка устройства', response);
    await fetchListDevices(actualSegment);
  };

  const revertDevice = async params => {
    const { data: response } = await api.post('/api/device/revert_device', { device_id: params.device_id });
    openNotification('Актуализация устройства', response);
    await fetchListDevices(actualSegment);
  };

  const softDelete = async device => {
    try {
      const { data: resData } = await api.post(`/api/device/delete/${device.device_id}`, device);
      if (resData) {
        await fetchListDevices(actualSegment);
      }
    } catch (error) {
      console.error('Ошибка внутри softDelete -> ', error);
      message.error('Ошибка при удалении устройства!');
    }
  };

  const clearLogin = async id => {
    try {
      const { data: resData } = await api.post(`/api/device/clear`, {
        id: id,
      });

      if (resData) {
        await fetchListDevices(actualSegment);
      }
    } catch (error) {
      console.log('ERROR - onFinishModal - forwarder>>>', error);
      if (error.message === 'Validation error') {
        message.info('ОШИБКА создания! Такой агент уже существует.');
      }
    }
  };

  const onFinish = async values => {
    if (actualSegment === DEVICES_TYPES.ACTUAL) {
      try {
        await api.patch(`/api/device/edit`, {
          id: clickRowId,
          call_sign: values['call_sign'],
          army_post: values['army_post'],
          password: values['password'],
          tg: values['tg'],
        });

        await fetchListDevices(actualSegment);
        setClickRowId();
        setNameBtn();
        form.resetFields();
      } catch (error) {
        console.log('error-getDeleteCell >>>', error, error.message);
      }
    }
  };

  const handleTableChange = async (page, pageSize) => {
    setLoading(true);
    setPage(page);
    setPageSize(pageSize);
    if (isAdmin) {
      return;
    }
    api
      .get(
        searcher
          ? `/api/device/find_with/${value}?actualSegment=${actualSegment}&offset=${page * 10}`
          : `/api/device/${actualSegment}/${userInfo.userId}?offset=${page * 10}`
      )
      .then(({ data: res }) => {
        setData(res.rows?.map((el, i) => ({ ...el, key: i + 1 })));
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <TourComponent
        handleClose={handleClose}
        handleOpen={startTour}
        onChange={handleTourChange}
        isTourOpen={isTourOpen}
        steps={steps}
        current={currentStep}
      />
      {contextHolder}
      <div className={styles.header}>
        <ActionModal text={actionModalText} callback={() => handleActionFunction()} />
        {!isMobile && (
          <TrustUserGroup
            device_id={trustUserGroupData.device_id ? trustUserGroupData.device_id : ''}
            readOnlySubs={trustUserGroupData.readOnlySubs ? trustUserGroupData.readOnlySubs : null}
            fetchListDevices={fetchListDevices}
            actualSegment={actualSegment}
            userInfo={userInfo}
            trustUserModalOpen={trustUserModalOpen}
            setTrustUserModalOpen={setTrustUserModalOpen}
            usersData={usersData}
          />
        )}
        <div ref={searchRef} className={styles.searchWrapper}>
          <SearchField
            setValue={setValue}
            setTotalData={setTotalData}
            setData={setData}
            setSearcher={setSearcher}
            actualSegment={actualSegment}
            fetchList={fetchListDevices}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setGenerateIdOpen={setGenerateIdOpen}
          />
        </div>
        <div className={styles.buttonBox} ref={generateIdRef}>
          <GenerateNewIdModal
            fetchListDevices={fetchListDevices}
            actualSegment={actualSegment}
            userInfo={userInfo}
            DebounceSelect={DebounceSelect}
            fetchUserList={fetchUserList}
            isGenerateIdOpen={isGenerateIdOpen}
            setGenerateIdOpen={setGenerateIdOpen}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </div>
      </div>
      {isAdmin && (
        <Segmented
          block
          defaultValue={DEVICES_TYPES.ACTUAL}
          options={DEVICES_TYPES_LIST}
          onChange={segment => {
            setActualSegment(segment);
            if (segment === DEVICES_TYPES.ONLINE) {
              fetchOnlineDevices();
            }
          }}
        />
      )}
      <Form form={form} onFinish={onFinish}>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            position: ['bottomCenter'],
            showSizeChanger: false,
            current: page,
            pageSize: pageSize,
            total: totalData,
            onChange: handleTableChange,
          }}
          scroll={
            isMobile
              ? null
              : {
                  x: 2000,
                }
          }
          bordered
        />
      </Form>
      {searcher ? (
        <FloatButton
          icon={<SyncOutlined />}
          tooltip={`Очистить фильтр`}
          onClick={() => {
            setSearcher(false);
            setPage(1);
          }}
        />
      ) : null}
    </>
  );
};

export default ForwarderTable;
